import { useEffect } from 'react';
import { Zap, MessageCircle, BarChart3, Target, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAnalysisContext } from '../../contexts/AnalysisContext';
import { trackEvent, Events } from '../../lib/posthog';
import { getGrade } from '../../lib/grades';
import { ExtractedFrames } from './ExtractedFrames';

const CATEGORY_LABELS = {
  setup: 'Setup',
  backswing: 'Backswing',
  transition: 'Transition',
  impact: 'Impact',
  followThrough: 'Follow-Through',
  efficiency: 'Efficiency',
  tempo: 'Tempo',
  repeatability: 'Repeatability',
  power: 'Power',
  consistency: 'Consistency',
};

export function FullAnalysis({
  swingHistory,
  openAskBirdie,
  setShowComparison,
}) {
  const {
    analysisResult,
    showFullAnalysis,
    setShowFullAnalysis,
    resetAnalysis,
  } = useAnalysisContext();

  useEffect(() => {
    if (showFullAnalysis && analysisResult) {
      trackEvent(Events.FULL_REPORT_VIEWED, {
        swingScore: analysisResult.swingScore?.overall,
      });
    }
  }, [showFullAnalysis, analysisResult]);

  if (!analysisResult || !showFullAnalysis) return null;

  const swingScore = analysisResult.swingScore || {};
  const score = swingScore.overall || 0;
  const grade = getGrade(score);
  const categories = swingScore.categories || {};
  const frameByFrame = analysisResult.frameByFrame || [];
  const whatsWorking = analysisResult.whatsWorking || [];
  const whatsCostingStrokes = analysisResult.whatsCostingStrokes || [];
  const projectedHandicap = analysisResult.projectedHandicap || {};
  const keyChallenges = analysisResult.keyChallenges || [];
  const criticalFix = analysisResult.criticalFix || {};

  // Old format fallback message
  const isOldFormat = !analysisResult.swingScore?.categories && !analysisResult.frameByFrame;

  return (
    <div>
      <div className="text-center mb-8">
        <button
          onClick={() => setShowFullAnalysis(false)}
          className="text-emerald-400 hover:text-emerald-300 text-sm mb-4 inline-flex items-center gap-1"
        >
          Back to Summary
        </button>
        <h2 className="text-3xl font-bold mb-2">Full Analysis</h2>
      </div>

      {/* Old format fallback */}
      {isOldFormat && (
        <div className="rounded-2xl bg-white/5 p-4 border border-white/10 mb-6 text-center">
          <p className="text-slate-400 text-sm">This analysis used an older format. Some sections may not be available.</p>
        </div>
      )}

      {/* 1. Uploaded Images */}
      <ExtractedFrames images={analysisResult.uploadedImages} />

      {/* 2. Frame-by-Frame Breakdown */}
      {frameByFrame.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wide mb-4">Frame-by-Frame Breakdown</h3>
          <div className="space-y-4">
            {frameByFrame.map((frame, i) => (
              <div key={i} className="rounded-2xl bg-white/5 p-5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center text-xs font-bold text-emerald-400">
                    {frame.frame}
                  </span>
                  <span className="text-sm font-bold text-white">{frame.phase}</span>
                </div>
                <div className="space-y-2">
                  {(frame.observations || []).map((obs, j) => (
                    <div key={j} className="pl-9">
                      <p className="text-slate-200 text-sm leading-relaxed">
                        <span className="text-white font-medium">{obs.see}</span>
                        {obs.means && <span className="text-slate-400"> - {obs.means}</span>}
                      </p>
                      {obs.impact && (
                        <p className="text-xs text-amber-400/80 mt-0.5">{obs.impact}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. Swing Score Card */}
      <div className="rounded-3xl bg-gradient-to-br from-emerald-500/10 to-green-500/10 p-6 border border-white/10 mb-8">
        <div className="text-center mb-5">
          <p className="text-sm text-slate-400 mb-1">SwingScore</p>
          <div className="text-5xl font-bold bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent mb-1">
            {score}
          </div>
          <p className="text-xl font-bold text-white">{grade}</p>
        </div>
        <div className="h-2 bg-white/10 rounded-full max-w-md mx-auto mb-6">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full" style={{width: `${score}%`}} />
        </div>

        {/* 10-category breakdown */}
        {Object.keys(categories).length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-lg mx-auto">
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
              const val = categories[key] || 0;
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-28 shrink-0">{label}</span>
                  <div className="flex-1 h-2 bg-white/10 rounded-full">
                    <div
                      className={`h-full rounded-full ${val >= 8 ? 'bg-emerald-500' : val >= 6 ? 'bg-green-500' : val >= 4 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${val * 10}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-300 w-5 text-right font-medium">{val}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 4. Projected Handicap */}
      {projectedHandicap.range && (
        <div className="rounded-2xl bg-white/5 p-5 border border-white/10 mb-6 text-center">
          <p className="text-sm text-slate-400 mb-1">Projected Handicap</p>
          <p className="text-3xl font-bold text-white mb-2">{projectedHandicap.range}</p>
          {projectedHandicap.reasoning && (
            <p className="text-sm text-slate-400">{projectedHandicap.reasoning}</p>
          )}
        </div>
      )}

      {/* 5. What's Working */}
      {whatsWorking.length > 0 && (
        <div className="rounded-2xl bg-emerald-500/10 p-5 sm:p-6 border border-emerald-500/20 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">WHAT'S WORKING</span>
          </div>
          <div className="space-y-3">
            {whatsWorking.map((item, i) => (
              <div key={i}>
                <p className="text-white font-medium text-sm">{item.strength}</p>
                {item.detail && <p className="text-slate-300 text-sm mt-0.5">{item.detail}</p>}
                {item.frames && <p className="text-xs text-emerald-400/60 mt-0.5">Frames: {item.frames}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. What's Costing Strokes */}
      {whatsCostingStrokes.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-4 px-1">
            What's Costing Strokes
          </p>
          {whatsCostingStrokes.map((item, idx) => (
            <div
              key={idx}
              className={`rounded-2xl p-5 sm:p-6 border mb-3 ${
                idx === 0
                  ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20'
                  : 'bg-white/5 border-white/10'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {idx === 0 && <Target className="h-4 w-4 text-amber-400" />}
                <span className={`text-sm font-bold ${idx === 0 ? 'text-amber-400' : 'text-slate-400'}`}>
                  #{item.rank || idx + 1} {item.issue}
                </span>
              </div>
              <p className="text-slate-200 text-sm leading-relaxed mb-1">{item.causes}</p>
              {item.whyItMatters && (
                <p className="text-xs text-slate-400">{item.whyItMatters}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 7. Key Challenges */}
      {keyChallenges.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-4 px-1">
            Key Challenges
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {keyChallenges.map((item, i) => (
              <div key={i} className="rounded-2xl bg-white/5 p-4 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                  <span className="text-sm font-bold text-white">{item.challenge}</span>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 8. One Critical Fix */}
      {criticalFix.fix && (
        <div className="rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 p-6 border border-green-500/20 mb-8">
          <div className="flex items-center justify-center gap-2 text-green-400 text-sm font-medium mb-3">
            <Zap className="h-4 w-4" />
            ONE CRITICAL FIX
          </div>
          <h3 className="text-xl font-bold text-white mb-3 text-center">{criticalFix.fix}</h3>

          {criticalFix.whyThisOne && (
            <p className="text-slate-300 text-sm mb-4 text-center">{criticalFix.whyThisOne}</p>
          )}

          {/* Swing Thought */}
          {criticalFix.swingThought && (
            <div className="rounded-xl bg-emerald-500/10 p-4 border border-emerald-500/20 mb-4 text-center">
              <p className="text-xs text-emerald-400 font-medium mb-1">SWING THOUGHT</p>
              <p className="text-lg text-white font-medium">"{criticalFix.swingThought}"</p>
            </div>
          )}

          {/* Drill */}
          {criticalFix.drill && (
            <div className="rounded-xl bg-black/20 p-4 border border-white/10">
              <p className="text-sm font-bold text-white mb-2">{criticalFix.drill.name}</p>
              {criticalFix.drill.howToPerform && (
                <p className="text-slate-300 text-sm mb-2">{criticalFix.drill.howToPerform}</p>
              )}
              {criticalFix.drill.reps && (
                <p className="text-xs text-slate-400 mb-1"><span className="text-slate-300 font-medium">Reps:</span> {criticalFix.drill.reps}</p>
              )}
              {criticalFix.drill.successLooksLike && (
                <p className="text-xs text-slate-400 mb-1"><span className="text-slate-300 font-medium">Success:</span> {criticalFix.drill.successLooksLike}</p>
              )}
              {criticalFix.drill.launchMonitorCheck && (
                <p className="text-xs text-slate-400"><span className="text-slate-300 font-medium">Launch monitor:</span> {criticalFix.drill.launchMonitorCheck}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 sm:gap-3 justify-center pt-6">
        <button
          onClick={openAskBirdie}
          className="rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-4 sm:px-6 py-3 font-bold text-white hover:scale-105 transition flex items-center gap-2 text-sm sm:text-base"
        >
          <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5" />
          Ask Birdie
        </button>
        {swingHistory.length > 1 && (
          <button
            onClick={() => setShowComparison(true)}
            className="rounded-xl bg-white/10 border border-white/20 px-4 sm:px-6 py-3 font-medium text-white hover:bg-white/20 transition flex items-center gap-2 text-sm sm:text-base"
          >
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />
            Compare
          </button>
        )}
        <button
          onClick={resetAnalysis}
          className="rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 px-4 sm:px-6 py-3 font-bold text-black hover:scale-105 transition text-sm sm:text-base"
        >
          New Lesson
        </button>
      </div>
    </div>
  );
}
